import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { customFetcher } from '../useFetch';

/**
 * Admin-Endpoints für das Dev-Tool `/admin/dev/processmanager`. Bewusst
 * getrennt von den Standard-PM-Hooks gehalten — die hier rufen explizit
 * die ``/admin/processmanager/*``-Routen, die rohen Edit ermöglichen
 * und nur unter dem ``[admin]``-Scope erreichbar sind.
 */

export interface IPmAdminAutomation {
  idProcessManagerAutomation: number;
  idProcessManagerAutomationType: number;
  /** JSON-String aus der DB — vom Frontend parsen bei Bedarf. */
  data: string | null;
}

export interface IPmAdminRow {
  idProcessManager: number;
  idProcessManagerType: number;
  ProcessManagerType?: string | null;
  idProcessManagerState: number;
  idLegalTransaction?: number | null;
  idLegalClient?: number | null;
  Created_at?: string | null;
  Updated_at?: string | null;
  DateToCheck?: string | null;
  LastTry_at?: string | null;
  Tries?: number | null;
  ExitCodeLastTry?: number | null;
  Closed_at?: string | null;
  /** Raw-JSON-String — Edit-Dialog parsed beim Öffnen. */
  data: string | null;
  Automations: IPmAdminAutomation[];
}

export interface IPmAdminListResult {
  rows: IPmAdminRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface IPmAdminFilter {
  page?: number;
  pageSize?: number;
  idProcessManagerType?: number | null;
  idProcessManagerState?: number | null;
  idLegalTransaction?: number | null;
  q?: string;
}

const buildQuery = (filter: IPmAdminFilter): string => {
  const params: string[] = [];
  if (filter.page) params.push(`page=${filter.page}`);
  if (filter.pageSize) params.push(`pageSize=${filter.pageSize}`);
  if (
    filter.idProcessManagerType !== undefined &&
    filter.idProcessManagerType !== null
  ) {
    params.push(`idProcessManagerType=${filter.idProcessManagerType}`);
  }
  if (
    filter.idProcessManagerState !== undefined &&
    filter.idProcessManagerState !== null
  ) {
    params.push(`idProcessManagerState=${filter.idProcessManagerState}`);
  }
  if (
    filter.idLegalTransaction !== undefined &&
    filter.idLegalTransaction !== null
  ) {
    params.push(`idLegalTransaction=${filter.idLegalTransaction}`);
  }
  if (filter.q && filter.q.trim() !== '') {
    params.push(`q=${encodeURIComponent(filter.q.trim())}`);
  }
  return params.length ? `?${params.join('&')}` : '';
};

const QK_LIST = (filter: IPmAdminFilter) => [
  'pmAdmin',
  'list',
  filter.page ?? 1,
  filter.pageSize ?? 25,
  filter.idProcessManagerType ?? null,
  filter.idProcessManagerState ?? null,
  filter.idLegalTransaction ?? null,
  (filter.q ?? '').toLowerCase().trim(),
];

export const useAdminPmList = (filter: IPmAdminFilter) => {
  const { data, isFetching, refetch } = useQuery<IPmAdminListResult>({
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: QK_LIST(filter),
    queryFn: () =>
      customFetcher({
        endpoint: `/admin/processmanager/list${buildQuery(filter)}`,
      }) as Promise<IPmAdminListResult>,
  } as never);
  return {
    pmListRows: data?.rows ?? [],
    pmListTotal: data?.total ?? 0,
    pmListPage: data?.page ?? 1,
    pmListPageSize: data?.pageSize ?? 25,
    isFetchingPmList: isFetching,
    refetchPmList: refetch,
  };
};

export const useAdminPmTrigger = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { triggered: number; sweepResult: unknown; after: IPmAdminRow | null },
    Error,
    number
  >({
    mutationFn: idPm =>
      customFetcher({
        endpoint: `/admin/processmanager/${idPm}/trigger`,
        method: 'POST',
      }) as Promise<{
        triggered: number;
        sweepResult: unknown;
        after: IPmAdminRow | null;
      }>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pmAdmin'] });
    },
  });
  return {
    triggerPm: mutateAsync,
    isTriggeringPm: isPending,
  };
};

export interface IPmAdminUpdateBody {
  data?: string | object | null;
  idProcessManagerState?: number;
  Tries?: number;
  ExitCodeLastTry?: number | null;
  LastTry_at?: string | null;
  Closed_at?: string | null;
  DateToCheck?: string | null;
}

export const useAdminPmUpdate = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { updated: number; after: IPmAdminRow },
    Error,
    { id: number; body: IPmAdminUpdateBody }
  >({
    mutationFn: ({ id, body }) =>
      customFetcher({
        endpoint: `/admin/processmanager/${id}`,
        method: 'PUT',
        body,
      }) as Promise<{ updated: number; after: IPmAdminRow }>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pmAdmin'] });
    },
  });
  return {
    updatePm: mutateAsync,
    isUpdatingPm: isPending,
  };
};

export const useAdminPmDelete = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { deleted: number },
    Error,
    number
  >({
    mutationFn: id =>
      customFetcher({
        endpoint: `/admin/processmanager/${id}`,
        method: 'DELETE',
      }) as Promise<{ deleted: number }>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pmAdmin'] });
    },
  });
  return {
    deletePm: mutateAsync,
    isDeletingPm: isPending,
  };
};

export interface IPmAdminExportResult {
  exportedAt: string;
  count: number;
  rows: IPmAdminRow[];
}

export const useAdminPmExport = () => {
  const { mutateAsync, isPending } = useMutation<
    IPmAdminExportResult,
    Error,
    IPmAdminFilter
  >({
    mutationFn: filter =>
      customFetcher({
        endpoint: '/admin/processmanager/export',
        method: 'POST',
        body: filter,
      }) as Promise<IPmAdminExportResult>,
  });
  return {
    exportPm: mutateAsync,
    isExportingPm: isPending,
  };
};

export const useAdminPmImport = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation<
    { inserted: number; updated: number; skipped: number },
    Error,
    { rows: unknown[] }
  >({
    mutationFn: body =>
      customFetcher({
        endpoint: '/admin/processmanager/import',
        method: 'POST',
        body,
      }) as Promise<{ inserted: number; updated: number; skipped: number }>,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pmAdmin'] });
    },
  });
  return {
    importPm: mutateAsync,
    isImportingPm: isPending,
  };
};
